import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { SHAPE_COMPONENT, SHAPE_FACTSHEET_BLUEPRINT } from '../../shapes'
import { withTranslation } from 'react-i18next'
import Icon from '../../../Icon'
import { getAttributeRequired, getComponentDefinition, getComponentLabel, getTrackingFactLabel } from '../../helper'
import './styles.scss'
import { Col, Row } from '../../../Grid'
import { Hours, HoursValidationError } from '../../AttributeValues/hours'
import AttributeHoursAdder from './attribute-hours-adder'
import Alert from '../../../Alert'
import SelectMultiple from '../../../Form/SelectMultiple'
import activityTrackingService from '../../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../../constants'
import FactsheetComponentLabel from '../factsheet-component-label'

const dayOptions = [
  { label: 'All', value: 'All' },
  { label: 'Monday', value: 'Mon' },
  { label: 'Tuesday', value: 'Tue' },
  { label: 'Wednesday', value: 'Wed' },
  { label: 'Thursday', value: 'Thu' },
  { label: 'Friday', value: 'Fri' },
  { label: 'Saturday', value: 'Sat' },
  { label: 'Sunday', value: 'Sun' },
  { label: 'Holidays', value: 'Holidays' },
]
const dayOptionsMap = new Map()
dayOptions.forEach(object => {
  dayOptionsMap.set(object.value, object)
})

class AttributeHours extends PureComponent {

  state = {
    hoursMapByDay: {},
    valid: true,
    required: false,
    errors: [],
    selectedDays: []
  }

  componentDidMount () {
    this.updateState()
  }

  componentDidUpdate (prevProps) {
    if (
      prevProps.data !== this.props.data
      || prevProps.blueprint !== this.props.blueprint
      || prevProps.factsheetBlueprint !== this.props.factsheetBlueprint
      || prevProps.factInstanceKey !== this.props.factInstanceKey) {
      this.updateState()
    }
  }

  updateState () {
    const factDefId = this.props.parentBlueprint.id
    const fact = this.props.factsheetBlueprint.factTree.facts[factDefId]
    const attributeDefId = this.props.blueprint.id
    const attribute = this.props.factsheetBlueprint.factTree.attributes[attributeDefId]
    const required = getAttributeRequired(this.props.blueprint, attribute, fact)
    let value = this.props.data?.value ?? ''
    let valid = !(!!required.attribute && value === '')
    let hours
    if (valid && value !== '') {
      try {
        hours = new Hours(value)
        valid = true
      } catch (e) {
        console.error(e)
        valid = false
      }
    }

    this.setState(
      {
        valid,
        required: !!required.attribute,
        hoursMapByDay: hours ? hours.getHoursMapByDay() : {}
      }
    )
    this.props.putValidationStatus(factDefId, this.props.factInstanceKey, attributeDefId, valid)

  }

  setErrorStateByError (e) {
    if (e instanceof HoursValidationError) {
      const { t } = this.props
      this.setState({ errors: [t(e.errorCode)] })
    } else {
      this.setState({ error: [e.message] })
    }
  }

  getNewHoursObject (selectedDays, time) {
    const hoursString = this.state.valid ? this.props.data?.value ?? '' : ''
    const hours = new Hours(hoursString)
    hours.addHours(selectedDays.join(','), time[0], time[1])
    return hours
  }

  updateHours (hoursObject) {
    const v = hoursObject.getConsolidated()
    if (v.length > 255) {
      this.setState({ error: 'You have exceeded the maximum number of values. Adding failed.' })
      return
    }
    if (v.length === 0) {
      this.props.deleteAttribute(
        this.props.parentBlueprint.id,
        this.props.factInstanceKey,
        this.props.blueprint.id
      )
      return
    }
    this.props.putAttribute(
      this.props.parentBlueprint.id,
      this.props.factInstanceKey,
      this.props.blueprint.id,
      {
        attributeDefId: this.props.blueprint.id,
        value: v
      })
  }

  onHoursAdded = (hoursToAdd) => {
    let newHoursObject
    try {
      newHoursObject = this.getNewHoursObject(this.state.selectedDays.map((v) => v.value), hoursToAdd)
    } catch (e) {
      this.setErrorStateByError(e)
      console.error(e)
      return
    }
    activityTrackingService.trackEvent(trackingEvent.editValue, {
      name: getTrackingFactLabel(this.props.parentBlueprint, this.props.factsheetBlueprint)
    })
    this.setState({ errors: [], selectedDays: [] })
    this.updateHours(newHoursObject)
  }

  onDaySelectChange = (selected) => {
    selected.length &&
    selected.find(option => option.value === 'All')
      ? this.setState({ selectedDays: dayOptions.slice(1) })
      : this.setState({ selectedDays: selected })
  }

  onHoursRemoveClick = (keyDay, keyHour) => {
    activityTrackingService.trackEvent(trackingEvent.editValue, {
      name: getTrackingFactLabel(this.props.parentBlueprint, this.props.factsheetBlueprint)
    })
    let hoursMapByDay = JSON.parse(JSON.stringify(this.state.hoursMapByDay))
    if (hoursMapByDay.hasOwnProperty(keyDay) && typeof hoursMapByDay[keyDay][keyHour] !== 'undefined') {
      hoursMapByDay[keyDay].splice(keyHour, 1)
    }

    let hoursString = ''
    for (const [key, value] of Object.entries(hoursMapByDay)) {
      if (value.length === 0) {
        continue
      }
      hoursString += key + ' ' + value.map((v) => v[0] + '-' + v[1]).join(',') + '\n'
    }

    const hours = new Hours(hoursString.replace(/^\n|\n$/g, ''))
    this.setState({ errors: [] })
    this.updateHours(hours)
  }

  render () {
    const attributeDefId = this.props.blueprint.id
    const attribute = this.props.factsheetBlueprint.factTree.attributes[attributeDefId]

    let value = ''
    if (typeof this.props.data !== 'undefined') {
      value = this.props.data.value
    }

    let label = getComponentLabel(
      this.props.blueprint,
      attribute
    )

    if (this.props.collapsed) {
      if (value === '') {
        return null
      }
      return <div className={'attribute-hours-collapsed'}>{label}: {value}</div>
    }

    const definition = getComponentDefinition(
      this.props.blueprint,
      attribute
    )
    let attrHoursList = []
    dayOptionsMap.forEach((value, key) => {
      if (this.state.hoursMapByDay.hasOwnProperty(key) && this.state.hoursMapByDay[key].length > 0) {
        attrHoursList.push(
          <div className={'attribute-hours-list-entry'} key={key}>
            <div className={'attribute-hours-list-entry-label'}>{value.label}</div>
            <div className={'attribute-hours-list-entry-values'}>
              {this.state.hoursMapByDay[key].map((v, keyHour) => {
                return <div className={'attribute-hours-list-entry-value'} key={keyHour}>
                  <div>{v[0]} - {v[1]}</div>
                  <div onClick={() => this.onHoursRemoveClick(key, keyHour)}><Icon type={'trash'}/></div>
                </div>
              })}
            </div>
          </div>
        )
      }
    })
    const { t } = this.props
    return (
      <div className={'attribute-hours'}>
        <div className={'attribute-hours-header'}>
          <FactsheetComponentLabel label={label} required={this.state.required} definition={definition}/>
        </div>
        <div className={'attribute-hours-container'}>
          <Row className={'attribute-hours-container-row'}>
            <Col xs={12} md={6}>
              <SelectMultiple
                onChange={this.onDaySelectChange}
                options={dayOptions}
                placeholder={t('DAY')}
                valid={true}
                selected={this.state.selectedDays}
              />
              <AttributeHoursAdder onAdd={this.onHoursAdded} addDisabled={
                this.state.to === null || this.state.from === null || this.state.selectedDays.length === 0
              }/>
            </Col>
            <Col xs={12} md={6}>
              <div className={'attribute-hours-list'}>
                {attrHoursList.length !== 0 ? attrHoursList :
                  <div className={'attribute-hours-list-message-empty'}>{t('NO_SERVICE_TIME_ADDED_YET')}</div>

                }
              </div>
            </Col>
          </Row>
        </div>
        {this.state.errors.length > 0 &&
          <div>
            {this.state.errors.map((error, key) => {
              return <Alert key={key} type={'danger'}>{error}</Alert>
            })}
          </div>
        }
        {this.state.valid === false &&
          <Alert type={'danger'}>Validation failed.</Alert>
        }
      </div>
    )
  }
}

AttributeHours.defaultProps = {}

AttributeHours.propTypes = {
  blueprint: PropTypes.shape(SHAPE_COMPONENT).isRequired,
  parentBlueprint: PropTypes.shape(SHAPE_COMPONENT).isRequired,
  factsheetBlueprint: PropTypes.shape(SHAPE_FACTSHEET_BLUEPRINT).isRequired,
  factInstanceKey: PropTypes.number.isRequired,
  data: PropTypes.object,
  collapsed: PropTypes.bool.isRequired,
  putAttribute: PropTypes.func.isRequired,
  deleteAttribute: PropTypes.func.isRequired,
  putValidationStatus: PropTypes.func.isRequired,
}
export default withTranslation()(AttributeHours)
